import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PackageCheck, Edit2, X, Save, Loader2, Lock, RotateCcw, Plus, AlertTriangle } from 'lucide-react'
import {
  getFulfillments, updateOrderFees, resetOrderFees, createManualFulfillment,
} from '../lib/api'
import { ToastContainer, useToast } from './Toast'
import { businessToday } from '../lib/dates'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const currentMonthStart = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

// ── Edit-fees modal ─────────────────────────────────────────────────────────

const FEE_FIELDS = [
  { key: 'pick_fee',           label: 'Pick' },
  { key: 'pack_fee',           label: 'Additional Items' },
  { key: 'handling_fee',       label: 'Labelling' },
  { key: 'insert_fee',         label: 'Insert' },
  { key: 'tag_fee',            label: 'Tag fee' },
  { key: 'shipping_cost',      label: 'Shipping (charged)' },
  { key: 'carrier_cost',       label: 'Carrier cost' },
]

function EditFeesModal({ row, onClose }) {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const [values, setValues] = useState(() => {
    const out = {}
    FEE_FIELDS.forEach(({ key }) => {
      const v = row[key]
      out[key] = v == null ? '' : String(v)
    })
    return out
  })

  const total = FEE_FIELDS.reduce((sum, { key }) => {
    if (key === 'carrier_cost') return sum   // carrier_cost is the 3PL's cost, not client charge
    const n = parseFloat(values[key])
    return sum + (Number.isFinite(n) ? n : 0)
  }, 0)

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {}
      FEE_FIELDS.forEach(({ key }) => {
        const raw = values[key]
        if (raw === '' || raw == null) return
        const n = parseFloat(raw)
        if (Number.isFinite(n)) payload[key] = n
      })
      return updateOrderFees(row.order_id, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fulfillments'] })
      addToast({ type: 'success', message: 'Fees updated — order is now locked from auto-pricing' })
      onClose()
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || err.message || 'Update failed',
    }),
  })

  const resetMut = useMutation({
    mutationFn: () => resetOrderFees(row.order_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fulfillments'] })
      addToast({ type: 'success', message: 'Manual lock cleared. Run Reprice Orders to apply current rate card.' })
      onClose()
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || err.message || 'Reset failed',
    }),
  })

  function handleChange(key, raw) {
    setValues(prev => ({ ...prev, [key]: raw }))
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
            <div>
              <h3 className="font-semibold text-slate-800">Edit Order Fees</h3>
              <p className="text-xs text-slate-400 mt-0.5 font-mono">
                Order {row.order_number || row.shipstation_order_id}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={18} />
            </button>
          </div>

          <form onSubmit={e => { e.preventDefault(); mutation.mutate() }} className="px-6 py-5 space-y-3">
            {FEE_FIELDS.map(({ key, label }) => (
              <div key={key} className="grid grid-cols-[1.4fr_1fr] gap-3 items-center">
                <label className="text-sm text-slate-600">{label}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={values[key]}
                    onChange={e => handleChange(key, e.target.value)}
                    className="input pl-6 text-sm font-mono text-right"
                    placeholder="0.00"
                  />
                </div>
              </div>
            ))}

            <div className="grid grid-cols-[1.4fr_1fr] gap-3 items-center pt-3 border-t border-slate-200">
              <span className="text-sm font-semibold text-slate-700">
                Client total <span className="text-xs text-slate-400 font-normal">(excl. carrier)</span>
              </span>
              <div className="text-right text-sm font-semibold text-slate-800 tabular-nums pr-3">
                {formatCurrency(total)}
              </div>
            </div>

            <p className="text-xs text-slate-400 pt-1 leading-relaxed">
              Saving locks the fees on this order. Reprice Orders and future syncs will leave the
              fees alone. Use <b>Reset to auto-pricing</b> to unlock — the order will pick up the
              current rate card on the next Reprice.
            </p>

            <div className="flex justify-between items-center gap-2 pt-2">
              {row.manually_edited_fees ? (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Clear the manual lock on this order? Reprice Orders will then overwrite these fees with the current rate card.')) {
                      resetMut.mutate()
                    }
                  }}
                  disabled={resetMut.isPending}
                  className="text-amber-600 hover:text-amber-700 text-xs flex items-center gap-1"
                >
                  {resetMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                  Reset to auto-pricing
                </button>
              ) : <div />}
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={mutation.isPending} className="btn-primary">
                  {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}

// ── Create manual fulfillment modal ─────────────────────────────────────────
// Same fee fields as EditFeesModal — admin enters them up-front rather than
// editing after-the-fact. Plus the metadata fields a real ShipStation
// fulfillment would carry (order #, ship date, recipient, carrier, tracking).

function CreateManualFulfillmentModal({ clientId, onClose }) {
  const today = businessToday()
  const [orderNumber,    setOrderNumber]    = useState('')
  const [shipDate,       setShipDate]       = useState(today)
  const [recipientName,  setRecipientName]  = useState('')
  const [recipientCity,  setRecipientCity]  = useState('')
  const [recipientState, setRecipientState] = useState('')
  const [carrierCode,    setCarrierCode]    = useState('')
  const [serviceCode,    setServiceCode]    = useState('')
  const [tracking,       setTracking]       = useState('')
  const [itemCount,      setItemCount]      = useState('1')
  const [fees, setFees] = useState(() => {
    const out = {}
    FEE_FIELDS.forEach(({ key }) => { out[key] = '' })
    return out
  })
  const [err, setErr] = useState(null)
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const total = FEE_FIELDS.reduce((sum, { key }) => {
    if (key === 'carrier_cost') return sum
    const n = parseFloat(fees[key])
    return sum + (Number.isFinite(n) ? n : 0)
  }, 0)

  const mutation = useMutation({
    mutationFn: () => {
      const feeNums = {}
      FEE_FIELDS.forEach(({ key }) => {
        const n = parseFloat(fees[key])
        feeNums[key] = Number.isFinite(n) ? n : 0
      })
      return createManualFulfillment(clientId, {
        order_number:    orderNumber.trim(),
        ship_date:       shipDate,
        recipient_name:  recipientName.trim() || null,
        recipient_city:  recipientCity.trim() || null,
        recipient_state: recipientState.trim() || null,
        carrier_code:    carrierCode.trim() || null,
        service_code:    serviceCode.trim() || null,
        tracking_number: tracking.trim() || null,
        item_count:      parseInt(itemCount, 10) || 1,
        ...feeNums,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fulfillments'] })
      addToast({ type: 'success', message: 'Manual self-ship order created.' })
      onClose()
    },
    onError: (e) => setErr(e.response?.data?.detail || e.message || 'Create failed.'),
  })

  function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!orderNumber.trim()) return setErr('Order number is required.')
    if (!shipDate)           return setErr('Ship date is required.')
    mutation.mutate()
  }

  function setFee(key, v) {
    setFees(prev => {
      const next = { ...prev, [key]: v }
      // Convenience: typing a carrier_cost mirrors into Shipping
      // (charged) when that field hasn't been touched yet, so admins
      // don't accidentally save a self-ship with $0 billable shipping.
      // They can still type a different value into Shipping after
      // — the mirror only fires while shipping_cost is empty.
      if (key === 'carrier_cost' && !prev.shipping_cost) {
        next.shipping_cost = v
      }
      return next
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
            <div>
              <h3 className="font-semibold text-slate-800">Add manual self-ship order</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                For orders that didn't flow through ShipStation. Fees are locked on save — Reprice won't touch them.
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Order identity */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Order #<span className="text-red-500"> *</span></label>
                <input type="text" value={orderNumber} onChange={e => setOrderNumber(e.target.value)}
                       className="input text-sm font-mono" placeholder="SO-12345" required autoFocus />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Ship date<span className="text-red-500"> *</span></label>
                <input type="date" value={shipDate} onChange={e => setShipDate(e.target.value)}
                       className="input text-sm" required />
              </div>
            </div>

            {/* Recipient */}
            <div className="grid grid-cols-[2fr_1fr_auto] gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Recipient</label>
                <input type="text" value={recipientName} onChange={e => setRecipientName(e.target.value)}
                       className="input text-sm" placeholder="Alice Smith" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Suburb</label>
                <input type="text" value={recipientCity} onChange={e => setRecipientCity(e.target.value)}
                       className="input text-sm" placeholder="Sydney" />
              </div>
              <div className="w-20">
                <label className="block text-xs font-medium text-slate-600 mb-1">State</label>
                <input type="text" value={recipientState} onChange={e => setRecipientState(e.target.value)}
                       className="input text-sm" placeholder="NSW" maxLength={4} />
              </div>
            </div>

            {/* Carrier */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Carrier</label>
                <input type="text" value={carrierCode} onChange={e => setCarrierCode(e.target.value)}
                       className="input text-sm" placeholder="auspost / fedex / dhl" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Service</label>
                <input type="text" value={serviceCode} onChange={e => setServiceCode(e.target.value)}
                       className="input text-sm" placeholder="EXPRESS" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Tracking</label>
                <input type="text" value={tracking} onChange={e => setTracking(e.target.value)}
                       className="input text-sm font-mono" placeholder="optional" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Items in order</label>
              <input type="number" min="1" step="1" value={itemCount}
                     onChange={e => setItemCount(e.target.value)}
                     className="input text-sm w-32" />
            </div>

            {/* Fees — exactly matches EditFeesModal */}
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Fees</p>
              {FEE_FIELDS.map(({ key, label }) => (
                <div key={key} className="grid grid-cols-[1.4fr_1fr] gap-3 items-center">
                  <label className="text-sm text-slate-600">{label}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input type="number" min="0" step="0.01" value={fees[key]}
                           onChange={e => setFee(key, e.target.value)}
                           className="input pl-6 text-sm font-mono text-right"
                           placeholder="0.00" />
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-[1.4fr_1fr] gap-3 items-center pt-2 border-t border-slate-200">
                <span className="text-sm font-semibold text-slate-700">
                  Client total <span className="text-xs text-slate-400 font-normal">(excl. carrier)</span>
                </span>
                <div className="text-right text-sm font-semibold text-slate-800 tabular-nums pr-3">
                  {formatCurrency(total)}
                </div>
              </div>
            </div>

            {err && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
                {err}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={mutation.isPending} className="btn-primary">
                {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Create order
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}


// ── Main tab ────────────────────────────────────────────────────────────────

export default function SelfShipTab({ clientId }) {
  const [fromDate, setFromDate] = useState(currentMonthStart())
  const [toDate, setToDate]     = useState('')
  const [editing, setEditing]   = useState(null)
  const [adding, setAdding]     = useState(false)

  const params = {
    ...(fromDate && { from: fromDate }),
    ...(toDate   && { to:   toDate   }),
  }

  // `error` is destructured deliberately: with only `data: rows = []`, a failed
  // fetch renders as "no self-ship orders in this period" and the totals row
  // disappears — a billable period that looks legitimately empty.
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['fulfillments', clientId, params],
    queryFn:  () => getFulfillments(clientId, params),
    enabled:  !!clientId,
  })

  const totals = rows.reduce((acc, r) => ({
    pick:         acc.pick         + (r.pick_fee         || 0),
    pack:         acc.pack         + (r.pack_fee         || 0),
    handling:     acc.handling     + (r.handling_fee     || 0),
    insert:       acc.insert       + (r.insert_fee       || 0),
    shipping:     acc.shipping     + (r.shipping_cost    || 0),
    carrier:      acc.carrier      + (r.carrier_cost     || 0),
    total:        acc.total        + (r.total_fulfilment || 0) + (r.shipping_cost || 0),
  }), { pick: 0, pack: 0, handling: 0, insert: 0, shipping: 0, carrier: 0, total: 0 })

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-slate-200">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Self-Ship Orders</h3>
          <p className="text-xs text-slate-400 mt-0.5">Orders marked as shipped in ShipStation without a carrier label</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <span className="text-slate-400 text-xs">to</span>
          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <button
            onClick={() => setAdding(true)}
            disabled={!clientId}
            className="btn-primary text-xs py-1 px-2.5 inline-flex items-center gap-1 ml-1"
            title="Add a self-ship order that didn't flow through ShipStation"
          >
            <Plus size={13} /> Add manual order
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Order #</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ship Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Recipient</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Carrier</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tracking</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Items</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Pick</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Pack</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Handling</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Insert</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Shipping</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Carrier Cost</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
              <th className="px-3 py-3 w-10" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 14 }).map((__, j) => (
                    <td key={j} className="px-4 py-3"><div className="skeleton h-4 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : error ? (
              <tr>
                <td colSpan={14} className="py-12 text-center">
                  <AlertTriangle size={28} className="mx-auto text-red-500 mb-2" />
                  <p className="text-slate-700 text-sm font-medium">Couldn't load self-ship orders</p>
                  <p className="text-slate-500 text-xs mt-1">
                    This is <b>not</b> an empty period — there may be self-ship orders here,
                    unshown and not counted in the totals.
                  </p>
                  <p className="text-red-600 text-xs mt-2 font-mono break-all px-6">
                    {error?.response?.data?.detail || error?.message || 'Unknown error'}
                  </p>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={14} className="py-12 text-center">
                  <PackageCheck size={28} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-400 text-sm">No self-ship orders in this period</p>
                  <p className="text-slate-300 text-xs mt-1">Run a Full Sync to pull fulfillments from ShipStation</p>
                </td>
              </tr>
            ) : (
              <>
                {rows.map(row => (
                  <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">
                      {row.order_number || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {row.ship_date || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-sm">
                      {row.recipient_name || '—'}
                      {(row.recipient_state) && (
                        <span className="text-slate-400 text-xs ml-1">{row.recipient_state}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs uppercase">
                      {row.carrier_code || '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">
                      {row.tracking_number || '—'}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-600 text-xs">
                      {row.item_count ?? '—'}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-600 text-xs">
                      {row.pick_fee > 0 ? formatCurrency(row.pick_fee) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-600 text-xs">
                      {row.pack_fee > 0 ? formatCurrency(row.pack_fee) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-600 text-xs">
                      {row.handling_fee > 0 ? formatCurrency(row.handling_fee) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-600 text-xs">
                      {row.insert_fee > 0 ? formatCurrency(row.insert_fee) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-600 text-xs">
                      {row.shipping_cost > 0 ? formatCurrency(row.shipping_cost) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-500 text-xs">
                      {row.carrier_cost > 0 ? formatCurrency(row.carrier_cost) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800 text-sm">
                      <span className="inline-flex items-center gap-1.5">
                        {row.manually_edited_fees && (
                          <Lock size={11} className="text-amber-500" title="Manually locked — Reprice/Sync won't change these fees" />
                        )}
                        {(() => {
                          const t = (row.total_fulfilment || 0) + (row.shipping_cost || 0)
                          return t > 0 ? formatCurrency(t) : <span className="text-slate-300">—</span>
                        })()}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      {row.order_id ? (
                        <button
                          onClick={() => setEditing(row)}
                          className={`transition-colors ${row.manually_edited_fees ? 'text-amber-500 hover:text-amber-700' : 'text-slate-300 hover:text-blue-600'}`}
                          title={row.manually_edited_fees ? 'Manually edited — click to view/edit' : 'Edit fees'}
                        >
                          <Edit2 size={14} />
                        </button>
                      ) : (
                        <span className="text-slate-200" title="No matching portal order to edit">—</span>
                      )}
                    </td>
                  </tr>
                ))}

                {/* Totals row */}
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={6} className="px-4 py-3 text-right text-sm font-semibold text-slate-600">
                    Total ({rows.length} order{rows.length !== 1 ? 's' : ''})
                  </td>
                  <td className="px-3 py-3 text-right text-xs font-semibold text-slate-700 tabular-nums">
                    {formatCurrency(totals.pick)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs font-semibold text-slate-700 tabular-nums">
                    {formatCurrency(totals.pack)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs font-semibold text-slate-700 tabular-nums">
                    {formatCurrency(totals.handling)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs font-semibold text-slate-700 tabular-nums">
                    {formatCurrency(totals.insert)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs font-semibold text-slate-700 tabular-nums">
                    {formatCurrency(totals.shipping)}
                  </td>
                  <td className="px-3 py-3 text-right text-xs font-semibold text-slate-500 tabular-nums">
                    {formatCurrency(totals.carrier)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-slate-800 text-sm tabular-nums">
                    {formatCurrency(totals.total)}
                  </td>
                  <td />
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditFeesModal row={editing} onClose={() => setEditing(null)} />
      )}
      {adding && (
        <CreateManualFulfillmentModal
          clientId={clientId}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  )
}
